import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Box,
  Alert,
  Typography,
  FormHelperText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { pb, getCurrentUser } from "../services/pocketbase";

// Add the formatDiscordMarkdown function
const formatDiscordMarkdown = (text) => {
  if (!text) return "";

  // Process complex formats first
  // Underline Bold Italic: __***text***__
  text = text.replace(
    /__\*\*\*(.*?)\*\*\*__/g,
    "<u><strong><em>$1</em></strong></u>"
  );

  // Underline Bold: __**text**__
  text = text.replace(/__\*\*(.*?)\*\*__/g, "<u><strong>$1</strong></u>");

  // Underline Italic: __*text*__
  text = text.replace(/__\*(.*?)\*__/g, "<u><em>$1</em></u>");

  // Bold Italic: ***text***
  text = text.replace(/\*\*\*(.*?)\*\*\*/g, "<strong><em>$1</em></strong>");

  // Then process simpler formats
  // Bold: **text**
  text = text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

  // Italic: *text* or _text_
  text = text.replace(/\*(.*?)\*|_(.*?)_/g, "<em>$1$2</em>");

  // Underline: __text__
  text = text.replace(/__(.*?)__/g, "<u>$1</u>");

  // Strikethrough: ~~text~~
  text = text.replace(/~~(.*?)~~/g, "<s>$1</s>");

  // Code blocks: ```text```
  text = text.replace(/```(.*?)```/gs, "<pre><code>$1</code></pre>");

  // Inline code: `text`
  text = text.replace(/`(.*?)`/g, "<code>$1</code>");

  // Line breaks
  text = text.replace(/\n/g, "<br />");

  return text;
};

const RankEditor = ({ open, onClose, rank = null }) => {
  const [formData, setFormData] = useState({
    position: "",
    title: "",
    description: "",
  });
  const [error, setError] = useState(null);
  const user = getCurrentUser();

  useEffect(() => {
    if (rank) {
      setFormData({
        position: rank.position || "",
        title: rank.title || "",
        description: rank.description || "",
      });
    } else {
      // For new ranks, set default values
      setFormData({
        position: "",
        title: "",
        description: "",
      });
    }
  }, [rank]);

  // Function to send webhook notification
  const sendWebhookNotification = async (action, newData, oldData = null) => {
    try {
      const timestamp = new Date().toISOString();
      let webhookData = {};

      if (action === "create") {
        webhookData = {
          embeds: [
            {
              title: "New Rank Created",
              color: 0x00ff00, // Green color
              fields: [
                {
                  name: "Position",
                  value: newData.position.toString(),
                  inline: true,
                },
                {
                  name: "Title",
                  value: newData.title || "No Title",
                  inline: true,
                },
                {
                  name: "Created By",
                  value: user?.username || "Unknown User",
                  inline: true,
                },
                {
                  name: "Description",
                  value: newData.description || "No Description",
                },
              ],
              timestamp: timestamp,
            },
          ],
        };
      } else if (action === "edit") {
        webhookData = {
          embeds: [
            {
              title: "Rank Edited",
              color: 0xffaa00, // Orange color
              fields: [
                {
                  name: "Edited By",
                  value: user?.username || "Unknown User",
                  inline: true,
                },
                {
                  name: "Timestamp",
                  value: new Date().toLocaleString(),
                  inline: true,
                },
                {
                  name: "Original Rank",
                  value: `**Position:** ${oldData.position}\n**Title:** ${
                    oldData.title || "No Title"
                  }\n**Description:** ${oldData.description || "No Description"}`,
                },
                {
                  name: "Updated Rank",
                  value: `**Position:** ${newData.position}\n**Title:** ${
                    newData.title || "No Title"
                  }\n**Description:** ${newData.description || "No Description"}`,
                },
              ],
              timestamp: timestamp,
            },
          ],
        };
      }

      // Send webhook notification
      await fetch("/api/webhooks/rank-logs", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(webhookData),
      });
    } catch (error) {
      console.error("Error sending webhook notification:", error);
      // Don't block the main operation if webhook fails
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const data = {
        position: parseInt(formData.position, 10),
        title: formData.title,
        description: formData.description,
      };

      if (rank?.id) {
        // Update existing rank
        await pb.collection("ranks").update(rank.id, data);
        // Send webhook notification for edit
        await sendWebhookNotification("edit", data, rank);
      } else {
        // For new ranks, check if we need to auto-assign a position
        if (!formData.position) {
          try {
            const records = await pb.collection("ranks").getList(1, 1, {
              sort: "-position",
            });

            if (records.items.length > 0) {
              data.position = records.items[0].position + 1;
            } else {
              data.position = 1;
            }
          } catch (err) {
            console.error("Error getting highest position:", err);
            data.position = 1;
          }
        }

        // Create new rank
        await pb.collection("ranks").create(data);
        // Send webhook notification for create
        await sendWebhookNotification("create", data);
      }

      onClose(true);
    } catch (err) {
      console.error("Error saving rank:", err);
      setError(err.message || "Failed to save rank");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      maxWidth="md"
      sx={{ zIndex: 99 }}
      fullWidth
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        {rank ? "Edit Rank" : "Add Rank"}
        <IconButton
          aria-label="close"
          onClick={() => onClose(false)}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              label="Position"
              type="number"
              value={formData.position}
              onChange={(e) =>
                setFormData({ ...formData, position: e.target.value })
              }
              helperText="Used for sorting ranks (lower numbers appear first)"
              required
            />

            <TextField
              fullWidth
              label="Rank Title"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
            />

            <Box>
              <TextField
                fullWidth
                label="Rank Description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                multiline
                rows={10}
              />
              <FormHelperText>
                Use Discord-style markdown: **bold**, *italic*, ***bold
                italic***, __underline__, __*underline italic*__, __**underline
                bold**__, __***underline bold italic***__, ~~strikethrough~~,
                `inline code`, ```code blocks```
              </FormHelperText>

              {formData.description && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    border: "1px solid #ccc",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Preview:
                  </Typography>
                  <Box
                    sx={{
                      whiteSpace: "pre-wrap",
                      "& code": {
                        backgroundColor: "rgba(0, 0, 0, 0.1)",
                        padding: "2px 4px",
                        borderRadius: "3px",
                        fontFamily: "monospace",
                      },
                      "& pre": {
                        backgroundColor: "rgba(0, 0, 0, 0.1)",
                        padding: "8px",
                        borderRadius: "4px",
                        overflowX: "auto",
                      },
                      "& strong": {
                        fontWeight: "bold",
                      },
                      "& em": {
                        fontStyle: "italic",
                      },
                      "& u": {
                        textDecoration: "underline",
                      },
                      "& s": {
                        textDecoration: "line-through",
                      },
                    }}
                    dangerouslySetInnerHTML={{
                      __html: formatDiscordMarkdown(formData.description),
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => onClose(false)}>Cancel</Button>
          <Button type="submit" variant="contained">
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default RankEditor;
